export const OPENAI_MAX_EDGE = 3840;
export const OPENAI_MAX_PIXELS = 8294400;

function parseAspectRatio(aspectRatio: string): { width: number; height: number } | null {
  if (aspectRatio === 'Auto') return null;
  const [width, height] = aspectRatio.split(':').map((part) => Number(part));
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  return { width, height };
}

function roundToMultipleOf16(value: number): number {
  return Math.max(16, Math.round(value / 16) * 16);
}

export function squareEdgeForImageSize(imageSize: string): number {
  if (imageSize === '1K') return 1024;
  if (imageSize === '2K') return 2048;
  return 2880;
}

export function longEdgeForImageSize(imageSize: string): number {
  if (imageSize === '1K') return 1536;
  if (imageSize === '2K') return 2048;
  return OPENAI_MAX_EDGE;
}

export function getOpenAiImageSize(aspectRatio: string, imageSize: string): string {
  const ratio = parseAspectRatio(aspectRatio);
  if (!ratio) {
    const edge = squareEdgeForImageSize(imageSize);
    return `${edge}x${edge}`;
  }

  const isSquare = ratio.width === ratio.height;
  if (isSquare) {
    const edge = squareEdgeForImageSize(imageSize);
    return `${edge}x${edge}`;
  }

  const requestedRatio = ratio.width / ratio.height;
  if (requestedRatio > 3 || requestedRatio < 1 / 3) {
    const edge = squareEdgeForImageSize(imageSize);
    return `${edge}x${edge}`;
  }

  const longEdge = longEdgeForImageSize(imageSize);
  let width = ratio.width > ratio.height ? longEdge : longEdge * requestedRatio;
  let height = ratio.height > ratio.width ? longEdge : longEdge / requestedRatio;

  const currentPixels = width * height;
  if (currentPixels > OPENAI_MAX_PIXELS) {
    const scale = Math.sqrt(OPENAI_MAX_PIXELS / currentPixels);
    width *= scale;
    height *= scale;
  }

  width = Math.min(OPENAI_MAX_EDGE, roundToMultipleOf16(width));
  height = Math.min(OPENAI_MAX_EDGE, roundToMultipleOf16(height));

  return `${width}x${height}`;
}
